import { FormControl, Stack, Typography } from '@mui/material';
import { Field, Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { TextField } from 'formik-mui';
import i18next from 'i18next';
import { filter, find, isEmpty, some } from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  AGENTS_VALIDATION_SCHEMA,
  AgentsFormFields,
  CommentField,
  CommissionIntervalForm,
  FormControls,
  FormikArray,
  FormikCheckbox,
  FormikNumericField,
  FormikSelect,
  FormikSelectOption,
  FormikTextField,
  HelpTooltip,
  INTERVAL_COMMISSION_VALIDATION_SCHEMA,
  UserSelect,
  usePostSavePrompt,
  useUnsavedChangesGuard,
} from 'components';
import { IP_REGEX } from 'constants/common.constants';
import {
  FUND_REQUEST_MAX_FEE_AMOUNT,
  FUND_REQUEST_MIN_FEE_AMOUNT,
} from 'constants/funds-requests.constants';
import { DEFAULT_ASSET_CURRENCY_CODE } from 'constants/order.constants';
import { ShopStatus } from 'enums';
import { ShopStatusSelect } from 'features/shop';
import { useCurrencies, useMeta, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { User } from 'types';

import {
  useManageShop,
  ManageShopSection,
  ManageShopTabActions,
} from '../components';
import { useShopSubmit } from '../hooks';

type GeneralValues = {
  name: string;
  userId: string;
  webSiteUrl: string;
  webSiteIp: string;
  shopIpWhitelist: string[];
  commission: number;
  intervalCommissions: any[];
  payoutCommission: number;
  status: ShopStatus;
  assetCurrencyId: string;
  fiatCurrencyId: string;
  payinCurrencyExchangeId: string;
  payinAssetCurrencyExchangeId: string;
  payinReverseAssetCurrencyExchangeId: string;
  payoutCurrencyExchangeId: string;
  agents: any[];
  isWithdrawalRestricted: boolean;
  withdrawalFeeAmount?: number | null;
  isInternalTransferRestricted: boolean;
  comment?: string;
};

type Props = {
  merchantUsers?: User[];
  traders?: User[];
};

export const GeneralTab: React.FC<Props> = ({ merchantUsers, traders }) => {
  const { shopId, shopData, isNew } = useManageShop();
  const { onSavedSuccess } = usePostSavePrompt();
  const {
    assetCurrenciesOptions,
    fiatCurrenciesOptions,
    currencyExchanges,
    defaultAssetCurrency,
    getCurrencyExchangeOptions,
    getDefaultCurrencyExchange,
    getAssetCurrencyExchangeOptions,
  } = useCurrencies();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();
  const { user, isAdmin, isMerchant, isAdminOrTechOperator } = useUser();
  const { isZeroFeesForbidden } = useMeta();

  const { handleSubmit: submitShop } = useShopSubmit({
    shopId,
    onSuccess: isNew ? undefined : onSavedSuccess,
  });

  const [currencyExchangeOptions, setCurrencyExchangeOptions] = useState<
    FormikSelectOption[]
  >([]);
  const [
    payinAssetCurrencyExchangeOptions,
    setPayinAssetCurrencyExchangeOptions,
  ] = useState<FormikSelectOption[]>([]);
  const [mappedFiatCurrenciesOptions, setMappedFiatCurrenciesOptions] =
    useState<FormikSelectOption[]>([]);

  const initialValues: GeneralValues = useMemo(() => {
    if (shopData) {
      return {
        name: shopData.name || '',
        userId: shopData.userId || '',
        webSiteUrl: shopData.webSiteUrl || '',
        webSiteIp: shopData.webSiteIp || '',
        shopIpWhitelist: shopData.shopIpWhitelist || [],
        commission: shopData.commission || 0,
        intervalCommissions: shopData.intervalCommissions || [],
        payoutCommission: shopData.payoutCommission || 0,
        status: shopData.status || ShopStatus.Inactive,
        assetCurrencyId: shopData.assetCurrencyId || '',
        fiatCurrencyId: shopData.fiatCurrencyId || '',
        payinCurrencyExchangeId: shopData.payinCurrencyExchangeId || '',
        payinAssetCurrencyExchangeId:
          shopData.payinAssetCurrencyExchangeId || '',
        payinReverseAssetCurrencyExchangeId:
          shopData.payinReverseAssetCurrencyExchangeId || '',
        payoutCurrencyExchangeId: shopData.payoutCurrencyExchangeId || '',
        agents: shopData.agents || [],
        isWithdrawalRestricted: shopData.isWithdrawalRestricted || false,
        withdrawalFeeAmount: shopData.withdrawalFeeAmount,
        isInternalTransferRestricted:
          shopData.isInternalTransferRestricted || false,
        comment: shopData.comment || '',
      };
    }
    return {
      name: '',
      userId: (isMerchant && user?.id) || '',
      webSiteUrl: '',
      webSiteIp: '',
      shopIpWhitelist: [],
      commission: (isMerchant && user?.transactionPercentage) || 0,
      intervalCommissions: [],
      payoutCommission: (isMerchant && user?.payoutTransactionPercentage) || 0,
      status: ShopStatus.Inactive,
      assetCurrencyId: assetCurrenciesOptions[0]?.value || '',
      fiatCurrencyId:
        filter(fiatCurrenciesOptions, (option) =>
          some(currencyExchanges, {
            assetCurrencyId: assetCurrenciesOptions[0]?.value || '',
            fiatCurrencyId: option.value,
          }),
        )?.[0]?.value || '',
      payinCurrencyExchangeId: '',
      payinAssetCurrencyExchangeId: '',
      payinReverseAssetCurrencyExchangeId: '',
      payoutCurrencyExchangeId: '',
      agents: [],
      isWithdrawalRestricted: false,
      isInternalTransferRestricted: false,
      comment: '',
    };
  }, [
    shopData,
    isMerchant,
    user,
    assetCurrenciesOptions,
    fiatCurrenciesOptions,
    currencyExchanges,
  ]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        webSiteUrl: Yup.string().required(tCommon('errors.required')),
        webSiteIp: Yup.string().matches(IP_REGEX, tCommon('errors.ip')),
        shopIpWhitelist: Yup.array().of(
          Yup.string()
            .required(i18next.t('errors.required'))
            .matches(IP_REGEX, tCommon('errors.ip')),
        ),
        assetCurrencyId: Yup.string().required(tCommon('errors.required')),
        fiatCurrencyId: Yup.string().required(tCommon('errors.required')),
        payinCurrencyExchangeId: Yup.string().nullable(),
        payinAssetCurrencyExchangeId: Yup.string().nullable().optional(),
        payinReverseAssetCurrencyExchangeId: Yup.string().nullable().optional(),
        payoutCurrencyExchangeId: Yup.string().nullable(),
        commission: Yup.number()
          .min(0, tCommon('errors.percentage'))
          .max(100, tCommon('errors.percentage'))
          .test(
            'zero-fee-forbidden',
            tCommon('errors.zero_fee_forbidden'),
            (value, context) =>
              !isZeroFeesForbidden ||
              context.parent.status !== ShopStatus.Active ||
              !!value,
          ),
        // zero fees forbidden is a payin-only restriction — payoutCommission is never checked for it
        payoutCommission: Yup.number()
          .min(0, tCommon('errors.percentage'))
          .max(100, tCommon('errors.percentage')),
        ...AGENTS_VALIDATION_SCHEMA,
        withdrawalFeeAmount: Yup.number()
          .min(
            FUND_REQUEST_MIN_FEE_AMOUNT,
            tCommon('errors.natural_or_zero_number'),
          )
          .max(
            FUND_REQUEST_MAX_FEE_AMOUNT,
            `${tCommon('errors.max')} ${FUND_REQUEST_MAX_FEE_AMOUNT}`,
          )
          .optional()
          .nullable(),
        ...(isAdminOrTechOperator && {
          userId: Yup.string().required(tCommon('errors.required')),
          intervalCommissions: INTERVAL_COMMISSION_VALIDATION_SCHEMA,
          comment: Yup.string().optional().nullable(),
        }),
      }),
    [tCommon, isAdminOrTechOperator, isZeroFeesForbidden],
  );

  const handleFormSubmit = useCallback(
    (values: GeneralValues, formikHelpers: FormikHelpers<GeneralValues>) => {
      const omitFields: string[] = [];
      if (isMerchant) {
        omitFields.push('userId');
      }
      if (!isNew) {
        omitFields.push('fiatCurrencyId', 'userId');
      }
      submitShop(values, formikHelpers, omitFields);
    },
    [isMerchant, isNew, submitShop],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleFormSubmit,
  });

  useUnsavedChangesGuard(formik);

  // Currency exchange options setup
  useEffect(() => {
    const assetCurrencyId = isNew
      ? initialValues.assetCurrencyId
      : shopData?.assetCurrencyId;
    const fiatCurrencyId = isNew
      ? initialValues.fiatCurrencyId
      : shopData?.fiatCurrencyId;

    if (assetCurrencyId && fiatCurrencyId) {
      setCurrencyExchangeOptions(
        getCurrencyExchangeOptions({ assetCurrencyId, fiatCurrencyId }),
      );
      setPayinAssetCurrencyExchangeOptions(
        getAssetCurrencyExchangeOptions(assetCurrencyId),
      );
    }
  }, [
    isNew,
    initialValues.assetCurrencyId,
    initialValues.fiatCurrencyId,
    shopData?.assetCurrencyId,
    shopData?.fiatCurrencyId,
    getCurrencyExchangeOptions,
    getAssetCurrencyExchangeOptions,
  ]);

  useEffect(() => {
    const assetCurrencyId = isNew
      ? initialValues.assetCurrencyId
      : shopData?.assetCurrencyId;
    if (assetCurrencyId) {
      setMappedFiatCurrenciesOptions(
        filter(fiatCurrenciesOptions, (option) =>
          some(currencyExchanges, {
            assetCurrencyId,
            fiatCurrencyId: option.value,
          }),
        ),
      );
    }
  }, [
    isNew,
    initialValues.assetCurrencyId,
    shopData?.assetCurrencyId,
    fiatCurrenciesOptions,
    currencyExchanges,
  ]);

  const isCurrencyExchangeSelectDisabled = useMemo(
    () => currencyExchangeOptions.length === 1,
    [currencyExchangeOptions],
  );

  const isFiatSelectDisabled = useMemo(
    () => mappedFiatCurrenciesOptions.length === 1 || !isNew,
    [mappedFiatCurrenciesOptions, isNew],
  );

  const handleCurrencyChange = useCallback(
    (value: string, name: string) => {
      const isAssetCurrencyChanged = name === 'assetCurrencyId';
      const assetCurrencyId = isAssetCurrencyChanged
        ? value
        : formik.values.assetCurrencyId;
      const isFiatCurrencyChanged = name === 'fiatCurrencyId';
      let fiatCurrencyId = isFiatCurrencyChanged
        ? value
        : formik.values.fiatCurrencyId;

      if (isAssetCurrencyChanged) {
        const newMappedFiatCurrenciesOptions = filter(
          fiatCurrenciesOptions,
          (option) =>
            some(currencyExchanges, {
              assetCurrencyId,
              fiatCurrencyId: option.value,
            }),
        );

        if (assetCurrencyId !== formik.initialValues.assetCurrencyId) {
          if (
            !find(
              newMappedFiatCurrenciesOptions,
              ({ value }) => value === fiatCurrencyId,
            )
          ) {
            fiatCurrencyId = newMappedFiatCurrenciesOptions[0].value;
            formik.setFieldValue('fiatCurrencyId', fiatCurrencyId);
          }
        } else {
          fiatCurrencyId = formik.initialValues.fiatCurrencyId;
          formik.setFieldValue('fiatCurrencyId', fiatCurrencyId);
        }

        setMappedFiatCurrenciesOptions(newMappedFiatCurrenciesOptions);
      }

      if (assetCurrencyId && fiatCurrencyId) {
        setCurrencyExchangeOptions(
          getCurrencyExchangeOptions({ assetCurrencyId, fiatCurrencyId }),
        );
      }

      if (
        assetCurrencyId !== formik.initialValues.assetCurrencyId ||
        fiatCurrencyId !== formik.initialValues.fiatCurrencyId
      ) {
        const defaultCurrencyExchangeId =
          (isAdminOrTechOperator &&
            getDefaultCurrencyExchange(assetCurrencyId, fiatCurrencyId)?.id) ||
          '';

        formik.setFieldValue(
          'payinCurrencyExchangeId',
          defaultCurrencyExchangeId,
        );
        formik.setFieldValue(
          'payoutCurrencyExchangeId',
          defaultCurrencyExchangeId,
        );
      } else {
        formik.setFieldValue(
          'payinCurrencyExchangeId',
          formik.initialValues.payinCurrencyExchangeId,
        );
        formik.setFieldValue(
          'payoutCurrencyExchangeId',
          formik.initialValues.payoutCurrencyExchangeId,
        );
      }
    },
    [
      formik,
      isAdminOrTechOperator,
      currencyExchanges,
      fiatCurrenciesOptions,
      getCurrencyExchangeOptions,
      getDefaultCurrencyExchange,
    ],
  );

  const renderIpWhiteListComponent = useCallback(
    ({ index }: { index: number }) => (
      <FormikTextField
        component={TextField}
        label={t('fields.website_ip')}
        name={`shopIpWhitelist[${index}]`}
        size="small"
        fullWidth
      />
    ),
    [t],
  );

  return (
    <FormikProvider value={formik}>
      <Form>
        {isAdminOrTechOperator && (
          <ManageShopSection>
            <FormControls>
              <ShopStatusSelect />
              <UserSelect
                name="userId"
                label={t('fields.merchant')}
                users={merchantUsers}
                required
                disabled={!isNew}
              />
            </FormControls>
          </ManageShopSection>
        )}

        <ManageShopSection>
          <FormControls>
            <Field
              component={TextField}
              label={t('fields.name')}
              name="name"
              size="small"
              required
              fullWidth
            />
            <Field
              component={TextField}
              label={t('fields.website_url')}
              name="webSiteUrl"
              size="small"
              required
              fullWidth
            />
            <Field
              component={TextField}
              label={t('fields.website_ip')}
              name="webSiteIp"
              size="small"
              fullWidth
            />
            <Typography variant="subtitle2">
              {t('fields.ip_whitelist_config')}
            </Typography>
            <FormikArray
              addItemLabel={t('fields.add_ip_btn')}
              name="shopIpWhitelist"
              removable
              ItemComponent={renderIpWhiteListComponent}
            />
          </FormControls>
        </ManageShopSection>

        {isAdminOrTechOperator && (
          <ManageShopSection title={t('sections.commissions')}>
            <Stack spacing={3}>
              <FormikNumericField
                label={t('fields.commission')}
                name="commission"
                allowNegative={false}
                percentageSuffix
                required
                fullWidth
              />
              <CommissionIntervalForm name="intervalCommissions" />
              <FormikNumericField
                label={t('fields.payout_commission')}
                name="payoutCommission"
                allowNegative={false}
                percentageSuffix
                required
                fullWidth
              />
            </Stack>
          </ManageShopSection>
        )}

        <ManageShopSection title={t('sections.currencies')}>
          <FormControls>
            <FormikSelect
              label={t('fields.currency')}
              name="assetCurrencyId"
              options={assetCurrenciesOptions}
              onChange={(value) =>
                handleCurrencyChange(value, 'assetCurrencyId')
              }
              showMetaError
              disabled={!isNew}
            />
            <FormikSelect
              label={t('fields.fiat_currency')}
              name="fiatCurrencyId"
              options={mappedFiatCurrenciesOptions}
              onChange={(value) =>
                handleCurrencyChange(value, 'fiatCurrencyId')
              }
              disabled={isFiatSelectDisabled}
            />
            {isAdminOrTechOperator && (
              <Fragment>
                {formik.values.assetCurrencyId &&
                  formik.values.assetCurrencyId !==
                    defaultAssetCurrency?.id && (
                    <Fragment>
                      <FormikSelect
                        label={t('fields.payin_asset_currency_exchange')}
                        name="payinAssetCurrencyExchangeId"
                        helperText={t(
                          'fields.payin_asset_currency_exchange_description',
                        )}
                        options={payinAssetCurrencyExchangeOptions}
                        noneOption
                      />
                      <FormikSelect
                        label={t(
                          'fields.payin_reverse_asset_currency_exchange',
                        )}
                        name="payinReverseAssetCurrencyExchangeId"
                        helperText={t(
                          'fields.payin_reverse_asset_currency_exchange_description',
                        )}
                        options={payinAssetCurrencyExchangeOptions}
                        noneOption
                      />
                    </Fragment>
                  )}
                {!isEmpty(currencyExchangeOptions) && (
                  <Fragment>
                    <FormikSelect
                      label={t('fields.payin_currency_exchange')}
                      name="payinCurrencyExchangeId"
                      options={currencyExchangeOptions}
                      noneOption
                      disabled={isCurrencyExchangeSelectDisabled}
                    />
                    <FormikSelect
                      label={t('fields.payout_currency_exchange')}
                      name="payoutCurrencyExchangeId"
                      options={currencyExchangeOptions}
                      noneOption
                      disabled={isCurrencyExchangeSelectDisabled}
                    />
                  </Fragment>
                )}
              </Fragment>
            )}
          </FormControls>
        </ManageShopSection>

        {isAdminOrTechOperator && (
          <ManageShopSection>
            <AgentsFormFields
              formError={formik.errors['agents'] as string | undefined}
              agents={formik.values.agents}
              users={traders}
              headerClassName="tw-text-base tw-font-semibold"
            />
          </ManageShopSection>
        )}

        {isAdminOrTechOperator && (
          <ManageShopSection title={t('fields.restrictions.title')}>
            <FormControls>
              <FormikCheckbox
                name="isWithdrawalRestricted"
                label={t('fields.restrictions.withdrawal')}
              />
              <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <FormikNumericField
                  name="withdrawalFeeAmount"
                  label={t('fields.withdrawal_fee_amount')}
                  fullWidth
                  suffix={DEFAULT_ASSET_CURRENCY_CODE}
                  disabled={!isAdmin}
                  allowNegative={false}
                  defaultValue={null}
                />
                <HelpTooltip
                  title={t('fields.withdrawal_fee_amount_tooltip')}
                />
              </FormControl>
              <FormikCheckbox
                name="isInternalTransferRestricted"
                label={t('fields.restrictions.internal_transfer')}
              />
            </FormControls>
          </ManageShopSection>
        )}

        {isAdminOrTechOperator && (
          <ManageShopSection>
            <FormControls>
              <CommentField
                label={t('fields.comment')}
                commentedAt={shopData?.commentedAt}
                commentedBy={shopData?.commentedBy}
              />
            </FormControls>
          </ManageShopSection>
        )}

        <ManageShopTabActions />
      </Form>
    </FormikProvider>
  );
};
